import { createAppointmentPhotos as createPhotos, PHOTO_CACHE_CONTROL, photoContentType, photoContentTypeFor, photoContentTypeOfExtension, takenPhotoMetadata, toAppointmentWorkflows, type ApiClient, type AppointmentPhotoPurpose, type PhotoSlot, type PhotoContentType, type PhotoUpload } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import type { AppointmentWorkflow, AppointmentWorkflowMetaData } from "@/models/appointment-task.model";

export { toAppointmentWorkflows, type AppointmentPhotoPurpose };

type PhotosApi = Pick<ApiClient["appointments"], "photos" | "photoSlots" | "recordPhoto" | "photoUpload">;

export type PutObject = (url: string, init: RequestInit) => Promise<Response>;

export type SlotPhoto = { workflow: uuid; isAfter: boolean; file: Blob; metadata: AppointmentWorkflowMetaData };

const UPLOAD_REFUSED = "The photo could not be uploaded. Try again.";

export const contentTypeOf = (file: Blob): PhotoContentType => photoContentTypeFor(file.type, file instanceof File ? file.name : null);

export const putWith =
  (put: PutObject) =>
  async ({ uploadUrl }: PhotoUpload, file: Blob, contentType: PhotoContentType): Promise<void> => {
    const answer = await put(uploadUrl, { method: "PUT", headers: { "content-type": contentType, "cache-control": PHOTO_CACHE_CONTROL }, body: file });
    if (!answer.ok) throw new Error(UPLOAD_REFUSED);
  };

export const putPhoto = putWith((url, init) => fetch(url, init));

export const PHOTO_FILE_TYPES = photoContentType.options.join(",");

export const UNSUPPORTED_PHOTO_FILE = "Choose a JPEG, PNG, WebP, HEIC, HEIF or GIF image.";

const extensionOfName = (name: string) => (name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "");

export const isPhotoFile = (file: File): boolean => photoContentType.safeParse(file.type).success || photoContentTypeOfExtension(extensionOfName(file.name)) !== undefined;

export function createAppointmentPhotos(api: PhotosApi, put: PutObject) {
  const photos = createPhotos<Blob>(api, { contentTypeOf, put: putWith(put) });

  return {
    read: (appointmentId: uuid): Promise<AppointmentWorkflow[]> => photos.read(appointmentId),

    slots: (appointmentId: uuid): Promise<PhotoSlot[]> => photos.slots(appointmentId),

    upload: (appointmentId: uuid, purpose: AppointmentPhotoPurpose, file: Blob): Promise<string> => photos.upload(appointmentId, purpose, file),

    recordSlotPhoto: (appointmentId: uuid, { metadata, ...slot }: SlotPhoto): Promise<AppointmentWorkflow[]> =>
      photos.recordSlotPhoto(appointmentId, { ...slot, metadata: takenPhotoMetadata(metadata) }),
  };
}
